// Small bottom-right toasts. Click one to follow its link (hand-offs) or dismiss it.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

export interface ToastInput {
  text: string;
  tone?: 'default' | 'error' | 'ok';
  href?: string;
  ms?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

const Ctx = createContext<(t: ToastInput) => void>(() => {});

export function useToast() {
  return useContext(Ctx);
}

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const navigate = useNavigate();

  const remove = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);

  const push = useCallback(
    (t: ToastInput) => {
      const id = nextId++;
      setItems((xs) => [...xs, { ...t, id }]);
      window.setTimeout(() => remove(id), t.ms ?? (t.href ? 9000 : 5000));
    },
    [remove],
  );

  const value = useMemo(() => push, [push]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => (
          <div
            key={t.id}
            className={`toast ${t.tone ?? ''}`}
            role="status"
            onClick={() => {
              remove(t.id);
              if (t.href) navigate(t.href);
            }}
          >
            {t.text}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
